

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const LegalDocuments = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`https://pp-backend-1-sa8p.onrender.com/api/legal-documents?propertyId=${propertyId}`);
        
        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }
        
        const data = await response.json();
        
        // Filter out documents without file URLs
        const validDocs = data.filter(doc => doc.fileUrl && doc.fileUrl.trim() !== '');
        setDocs(validDocs);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [propertyId]);

  const handlePreview = (docId) => {
    window.open(`https://pp-backend-1-sa8p.onrender.com/api/legal-documents/${docId}/preview`, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (docId, docName) => {
    const link = document.createElement('a');
    link.href = `https://pp-backend-1-sa8p.onrender.com/api/legal-documents/${docId}/download`;
    link.download = `${docName}.pdf`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Wrapper>
        <Header>
          <Title>Legal Documents</Title>
        </Header>
        <LoadingMessage>Loading documents...</LoadingMessage>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Header>
          <Title>Legal Documents</Title>
        </Header>
        <ErrorMessage>Error: {error}</ErrorMessage>
        <RetryButton onClick={() => window.location.reload()}>
          Try Again
        </RetryButton>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <BackButton onClick={() => navigate(-1)}>← Back</BackButton>
        <Title>Legal Documents</Title>
      </Header>
      
      <StatusBadge>
        <VerifiedBadge>Verified</VerifiedBadge>
        <LastUpdated>Last Updated: {new Date().toLocaleDateString()}</LastUpdated>
      </StatusBadge>
      
      {docs.length > 0 ? (
        <DocumentsGrid>
          {docs.map(doc => (
            <DocumentCard key={doc._id}>
              <DocumentHeader>
                <DocumentName>{doc.name}</DocumentName>
                <DocumentCategory>{doc.category}</DocumentCategory>
              </DocumentHeader>
              
              <DocumentActions>
                <ActionButton 
                  onClick={() => handlePreview(doc._id)}
                  $type="preview"
                >
                  Preview
                </ActionButton>
                <ActionButton 
                  onClick={() => handleDownload(doc._id, doc.name)}
                  $type="download"
                >
                  Download
                </ActionButton>
              </DocumentActions>
            </DocumentCard>
          ))}
        </DocumentsGrid>
      ) : (
        <NoDocumentsMessage>No legal documents available for this property</NoDocumentsMessage>
      )}
      
      <Disclaimer>
        <DisclaimerTitle>Disclaimer</DisclaimerTitle>
        <DisclaimerText>
          These documents are provided for informational purposes only. We recommend consulting with a legal professional before making any decisions based on these documents.
        </DisclaimerText>
      </Disclaimer>
    </Wrapper>
  );
};

// Styled Components
const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  margin-right: 1rem;
  color: #2c3e50;
  transition: color 0.2s;

  &:hover {
    color: #1a73e8;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2c3e50;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const VerifiedBadge = styled.span`
  background-color: #4caf50;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const LastUpdated = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const DocumentCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const DocumentHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const DocumentName = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
`;

const DocumentCategory = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
`;

const DocumentActions = styled.div`
  display: flex;
  padding: 1rem 1.5rem;
  gap: 1rem;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$type === 'preview' && `
    background-color: #e8f0fe;
    color: #1a73e8;
    
    &:hover {
      background-color: #d2e3fc;
    }
  `}
  
  ${props => props.$type === 'download' && `
    background-color: #1a73e8;
    color: white;
    
    &:hover {
      background-color: #1765cc;
    }
  `}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
  font-size: 1.1rem;
  background-color: #fde8e8;
  border-radius: 8px;
  margin: 1rem 0;
`;

const RetryButton = styled.button`
  display: block;
  margin: 1rem auto;
  padding: 0.75rem 1.5rem;
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #1765cc;
  }
`;

const NoDocumentsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 1rem 0;
`;

const Disclaimer = styled.div`
  margin-top: 3rem;
  padding: 1.5rem;
  background-color: #fff8e1;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
`;

const DisclaimerTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #ff9800;
`;

const DisclaimerText = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
`;

export default LegalDocuments;